import React, { useContext, useEffect, useState } from "react";
import List, { STATE } from "../../List/index";
import SecondaryPagination from "../../SecondaryPagination/SecondaryPagination";

import { ReactComponent as EyeSvg } from "../../../assets/admin/icons/eye.svg";

import SearchBox from "../SearchBox";
import useUsers from "../../../hooks/useUsers";
import useCertificates from "../../../hooks/useCertificates";
import useCompanies from "../../../hooks/useCompanies";
import { format } from "date-fns";
import useLandingPage from "../../../hooks/useLandingPage";
import Modal from "./Certificate";
import AppContext from "../../../AppContext";

const columns = [
  {
    display: "#",
    key: "no",
    textAlign: "left",
    maxWidth: 20,
  },
  {
    display: "User / Company",
    key: "user",
    textAlign: "left",
  },
  {
    display: "Certificate title",
    key: "name",
    textAlign: "left",
  },
  {
    display: "Issuer",
    key: "issuer",
    textAlign: "left",
  },
  {
    display: "Created on",
    key: "createdOn",
    textAlign: "left",
  },
  {
    display: "Status",
    key: "status",
    textAlign: "left",
  },
  {
    display: "",
    key: "actions",
    textAlign: "left",
    maxWidth: 50,
  },
];

const Certificates = () => {
  const [dataState, setDataState] = useState(STATE.LOADING);
  const [certificates, setCertificates] = useState(undefined);
  const [metadata, setMetadata] = useState(undefined);
  const [limit, setLimit] = useState(10);
  const [page, setPage] = useState(1);
  const [filter, setFilter] = useState(undefined);
  const [selectedCert, setSelectedCert] = useState(undefined);

  const { queryUsers } = useUsers();
  const { queryCertificate } = useCertificates();
  const { queryCompany } = useCompanies();

  useEffect(() => {
    fetchData();
    return () => {};
  }, []);

  useEffect(() => {
    fetchData();
  }, [filter, limit, page]);

  // * Fetch data
  const fetchData = async () => {
    const _filter = [{ active: true }, { deleted: false }];

    // * Add filter if present
    if (filter) {
      _filter.unshift(filter);
    }

    const { data, _metadata } = await queryCertificate(
      {
        $and: _filter,
      },
      page,
      limit,
      null,
      { createdOn: 1 }
    );

    let userIds = [];
    let companyIds = [];

    // * Map data to display
    let _certificates = data.map(
      (
        { _id, name, issuer, status, user, company, createdBy, createdOn },
        i
      ) => {
        if (user) userIds.push(user);
        if (company) companyIds.push(company);

        return {
          no: (page - 1) * limit + i + 1,
          name,
          issuer,
          createdOn: format(new Date(createdOn), "dd/MM/yyyy"),
          status: getUserStatus(status),
          actions: (
            <div className="admin-verification-users-lists-actions">
              <div
                className="admin-verification-users-lists-actions-view"
                onClick={() => handleViewUserClick({ certId: _id })}
              >
                <EyeSvg />
              </div>
            </div>
          ),
          user,
          company,
          createdBy,
        };
      }
    );

    Promise.all([]);

    const { data: users } = await queryUsers(
      {
        _id: {
          $in: userIds,
        },
      },
      1,
      userIds.length,
      {
        firstname: 1,
        surname: 1,
      }
    );

    const { data: companys } = await queryCompany(
      {
        _id: {
          $in: companyIds,
        },
      },
      1,
      companyIds.length,
      {
        name: 1,
      }
    );

    _certificates = _certificates.map((c) => {
      if (c.user) {
        const _user = users.find((u) => u._id === c.user);

        if (!_user) {
          return {
            ...c,
            user: c.user,
          };
        }

        return {
          ...c,
          user: `${_user.firstname} ${_user.surname || ""}`,
        };
      }

      if (c.company) {
        const _company = companys.find((u) => u._id === c.company);

        if (!_company) {
          return {
            ...c,
            company: c.company,
            user: "",
          };
        }

        return {
          ...c,
          company: `${_company.name}`,
          user: _company.name,
        };
      }

      return {
        ...c,
        company: "",
        user: "",
      };
    });

    setCertificates(_certificates);
    setMetadata(_metadata);
    setDataState(STATE.IDLE);
  };

  const getUserStatus = (status) => {
    if (status === 1) {
      return (
        <div className="admin-verification-users-lists-status admin-verification-users-lists-status--pending">
          Pending
        </div>
      );
    }

    if (status === 2) {
      return (
        <div className="admin-verification-users-lists-status admin-verification-users-lists-status--approved">
          Approved
        </div>
      );
    }

    return (
      <div className="admin-verification-users-lists-status admin-verification-users-lists-status--rejected">
        Rejected
      </div>
    );
  };

  const handleOnPageChange = (page) => setPage(page);

  const handleOnLimitChange = (limit) => {
    setPage(1);
    setLimit(limit);
  };

  const handleSearchChange = async (value) => {
    if (!value) {
      setFilter({});
      return;
    }

    // * Query users first then use users found to set filters
    const [userResult, compantResult] = await Promise.all([
      queryUsers(
        {
          $and: [
            {
              $or: [
                {
                  no: isNaN(value) ? 0 : +value,
                },
                {
                  firstname: { $regex: value },
                },
                {
                  surname: { $regex: value },
                },
              ],
            },
            { deleted: false },
          ],
        },
        null,
        -1,
        { _id: 1 }
      ),
      queryCompany({
        name: {
          $regex: value,
        },
        deleted: false,
      }),
    ]);

    let _filters = [];

    if (userResult.data.length > 0) {
      _filters.push({ user: { $in: userResult.data.map((u) => u._id) } });
    }

    if (compantResult.data.length > 0) {
      _filters.push({ company: { $in: compantResult.data.map((u) => u._id) } });
    }

    setPage(1);
    setFilter({
      $or: _filters,
    });
  };

  const handleViewUserClick = (cert) => {
    setSelectedCert(cert);
  };

  const handleCloseViewUser = () => {
    setSelectedCert(undefined);
    fetchData();
  };

  return (
    <>
      <div className="admin-lp-hb">
        <div className="admin-lp-hb-header">
          <SearchBox placeholder="UID or name" onChange={handleSearchChange} />
        </div>
        <List
          contentState={dataState}
          columns={columns}
          dataSource={certificates}
          viewable={false}
        >
          <SecondaryPagination
            limits={[10, 50]}
            defaultPage={metadata?.currentPage || 1}
            pages={metadata?.totalPages || 1}
            defaultLimit={metadata?.pageSize}
            onPageChange={handleOnPageChange}
            onLimitChange={handleOnLimitChange}
          />
        </List>
      </div>
      {selectedCert && (
        <Modal cert={selectedCert} onClose={handleCloseViewUser} />
      )}
    </>
  );
};

Certificates.propTypes = {};

export default Certificates;
